import React, { useState,useEffect } from 'react';
import { Heart, Phone, MapPin, Menu, X, Mail, Facebook, Instagram, Clock, Award, Shield, MessageCircle, Users, Target, Eye, Check, Home, Smile, HandHeart, Star } from 'lucide-react';

export default function AboutPage() {
  useEffect(() => {
    // Scroll to top when this page loads
    window.scrollTo(0, 0);
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const values = [
    {
      icon: <Heart className="w-10 h-10" />,
      title: "Compassionate Care",
      description: "We treat every client with dignity, respect, and genuine compassion, understanding their unique needs.",
      color: "#f5a09e",
      bgColor: "#ffe8e6"
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: "Patient-Centered Approach",
      description: "Each care plan is tailored to meet individual requirements, ensuring personalized attention for every client.",
      color: "#65c063",
      bgColor: "#dcfce7"
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Trusted Professionals",
      description: "Our experienced team is committed to delivering exceptional care with the highest standards of professionalism.",
      color: "#f5a09e",
      bgColor: "#ffe8e6"
    },
    {
      icon: <Smile className="w-10 h-10" />,
      title: "Quality of Life",
      description: "We focus on promoting independence, well-being, and meaningful connections for all our clients.",
      color: "#65c063",
      bgColor: "#dcfce7"
    }
  ];

  const services = [
    "Housekeeping and home maintenance",
    "Companionship and social engagement",
    "Clean living environment management",
    "Respite care for family caregivers",
    "Personal hygiene assistance",
    // "Reliable transportation services"
  ];

  const stats = [
    { number: "5+", label: "Years Experience", color: "#65c063" },
    // { number: "50+", label: "Happy Clients", color: "#f5a09e" },
    { number: "24/7", label: "Availability", color: "#65c063" },
    { number: "100%", label: "Satisfaction", color: "#f5a09e" }
  ];

  return (
    <div className="min-h-screen bg-white">
     

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #fff5f4 50%, #ffe8e6 100%)'}}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" style={{backgroundColor: '#65c063'}}></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl" style={{backgroundColor: '#f5a09e'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 rounded-full text-sm font-medium text-white" style={{background: 'linear-gradient(135deg, #65c063, #29a060)'}}>
              About Heart to Heart Home Care Services
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            We Take Care of Your
            <br />
            <span style={{color: '#f5a09e'}}>Problems </span>
            <span style={{color: '#65c063'}}>Carefully</span>
          </h1>
          
          <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
            Welcome to Heart to Heart Home Care Services, providing personalized, patient-centered care for the past 5 years. 
            We understand each client's unique requirements and offer compassionate support to meet their individual needs.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-2xl" style={{backgroundColor: index % 2 === 0 ? '#dcfce7' : '#ffe8e6'}}>
                <div className="text-5xl font-bold mb-2" style={{color: stat.color}}>{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-20" style={{background: 'linear-gradient(135deg, #fff5f4 0%, #f0fdf4 100%)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full blur-2xl" style={{backgroundColor: '#65c063'}}></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full blur-2xl" style={{backgroundColor: '#f5a09e'}}></div>
              <img 
                src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?q=80&w=800" 
                alt="Care team"
                className="rounded-3xl shadow-2xl relative z-10 w-full"
              />
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-white p-6 rounded-2xl shadow-xl z-20 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #65c063, #29a060)'}}>
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-xl">5 Years</div>
                  <div className="text-sm text-gray-600">of Excellence</div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <span className="font-semibold text-sm uppercase tracking-wider" style={{color: '#f5a09e'}}>Our Story</span>
                <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6">
                  A Legacy of Compassionate Care
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  From housekeeping and companionship to maintaining a clean living environment, respite care, personal hygiene assistance, 
                  , our dedicated team is here to enhance the quality of life for your loved ones.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  With a legacy of 5 years in the industry, Heart to Heart Home Care Services has built a reputation for delivering 
                  exceptional care and attention. Our experienced team is committed to making a positive impact on the lives of our clients, 
                  promoting independence, well-being, and meaningful connections.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We take pride in our personalized approach, ensuring that every client receives the utmost compassion and professionalism 
                  they deserve. Trust Heart to Heart Home Care Services to provide the highest level of care, tailored to meet the unique 
                  needs of your loved ones.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-semibold text-sm uppercase tracking-wider" style={{color: '#65c063'}}>What We Do</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">Our Comprehensive Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join our compassionate community and experience the difference of tailored care. 
              Contact us today for comprehensive support every step of the way.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 rounded-2xl border-2 border-gray-100 hover:shadow-lg transition-all"
                style={{backgroundColor: index % 2 === 0 ? '#dcfce7' : '#ffe8e6'}}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: index % 2 === 0 ? '#65c063' : '#f5a09e'}}>
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{service}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20" style={{background: 'linear-gradient(135deg, #fff5f4 0%, #f0fdf4 100%)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-semibold text-sm uppercase tracking-wider" style={{color: '#f5a09e'}}>Our Values</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">
              What Makes Us Different
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our core values guide everything we do, ensuring the highest quality care for your loved ones.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="flex items-start gap-6 p-8 rounded-3xl border-2 border-gray-100 hover:shadow-2xl transition-all hover:-translate-y-2"
                style={{backgroundColor: value.bgColor}}
              >
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-white" style={{backgroundColor: value.color}}>
                  {value.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="p-10 rounded-3xl" style={{background: 'linear-gradient(135deg, #65c063, #29a060)'}}>
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-6">
                <Target className="w-8 h-8" style={{color: '#65c063'}} />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-green-50 leading-relaxed text-lg">
                To provide personalized, patient-centered care that enhances the quality of life for every client. 
                We are committed to understanding unique needs and delivering compassionate support with professionalism and dedication.
              </p>
            </div>

            <div className="p-10 rounded-3xl" style={{background: 'linear-gradient(135deg, #f5a09e, #e88580)'}}>
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-6">
                <Eye className="w-8 h-8" style={{color: '#f5a09e'}} />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-pink-50 leading-relaxed text-lg">
                To be the most trusted home care provider in the community, known for exceptional service, compassionate caregivers, 
                and making a positive impact on the lives of our clients and their families.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white relative overflow-hidden" style={{background: 'linear-gradient(135deg, #f5a09e 0%, #e88580 50%, #f5a09e 100%)'}}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">
            Join Our Compassionate Community
          </h2>
          <p className="text-xl text-pink-50 mb-8">
            Experience the difference of tailored care. Contact us today for comprehensive support every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" href="tel:+16045127113">
            <button className="bg-white px-8 py-4 rounded-full font-medium hover:shadow-2xl transition-all flex items-center justify-center gap-2" style={{color: '#f5a09e'}}>
              <Phone className="w-5 h-5"  />
              
              Call: +1 604-512-7113
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-full font-medium hover:bg-white transition-all flex items-center justify-center gap-2 hover:text-[#f5a09e]">
              <Mail className="w-5 h-5" />
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/16047226460"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform group"
        style={{background: '#25D366'}}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
        <span className="absolute right-16 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
          Chat with us
        </span>
      </a>

    
    </div>
  );
}